import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Alert,
  Tag,
  Input,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { formatCurrency } from '../../utils/formatters';
import dayjs from 'dayjs';
import DateRangeFilter from '../../components/DateRangeFilter';

const { Option } = Select;

const PaymentList = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpense: 0,
    totalIncomeVAT: 0,
    totalExpenseVAT: 0,
    filteredIncome: 0,
    filteredExpense: 0,
    filteredIncomeVAT: 0,
    filteredExpenseVAT: 0,
  });
  const [dateFilter, setDateFilter] = useState({
    year: dayjs().year(),
    month: dayjs().month(),
    dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
  });
  const [filters, setFilters] = useState({
    status: [],
    dateRange: null,
    plPosition: '',
    category: '',
    channel: '',
    van: '',
    search: '',
  });
  const [channels, setChannels] = useState([]);
  const [vans, setVans] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsRes, vansRes] = await Promise.all([
          apiService.channels.getAll(),
          apiService.vans.getAll()
        ]);
        setChannels(channelsRes.data || []);
        setVans(vansRes.data || []);
      } catch (err) {
        console.error('Failed to fetch data', err);
      }
    };
    fetchData();
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        status: filters.status.length > 0 ? filters.status : undefined,
        ...(dateFilter.month === 'custom' 
          ? {
              startDate: dateFilter.dateRange[0]?.format('YYYY-MM-DD'),
              endDate: dateFilter.dateRange[1]?.format('YYYY-MM-DD'),
            }
          : {
              year: dateFilter.year,
              month: dateFilter.month,
            }
        ),
        plPosition: filters.plPosition,
        category: filters.category,
        channel: filters.channel,
        van: filters.van,
        search: filters.search,
      };
      const response = await apiService.payments.getAll(params);
      console.log('Payments response:', response);

      // New backend structure
      const {
        data: paymentsData = [],
      } = response?.data || {};

      setPayments(paymentsData);

      // Calculate income and expenses
      const calculateTotals = (payments) => {
        return payments.reduce((acc, payment) => {
          if (payment.plPosition === 'income') {
            acc.income += payment.totalAmount || 0;
            acc.incomeVAT += payment.vatAmount || 0;
          } else if (payment.plPosition === 'expense') {
            acc.expense += payment.totalAmount || 0;
            acc.expenseVAT += payment.vatAmount || 0;
          }
          return acc;
        }, { income: 0, expense: 0, incomeVAT: 0, expenseVAT: 0 });
      };

      // Calculate totals for all payments and filtered payments
      const allTotals = calculateTotals(paymentsData);
      const filteredTotals = calculateTotals(paymentsData);

      setStats({
        totalIncome: allTotals.income,
        totalExpense: allTotals.expense,
        totalIncomeVAT: allTotals.incomeVAT,
        totalExpenseVAT: allTotals.expenseVAT,
        filteredIncome: filteredTotals.income,
        filteredExpense: filteredTotals.expense,
        filteredIncomeVAT: filteredTotals.incomeVAT,
        filteredExpenseVAT: filteredTotals.expenseVAT,
      });
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError(error.response?.data?.message || 'Failed to fetch payments');
      message.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [filters, dateFilter]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDelete = async (id) => {
    try {
      await apiService.payments.delete(id);
      message.success('Payment deleted successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      message.error('Failed to delete payment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text, record) => (
        <a
          href={`/payments/${record._id}`}
          style={{ color: '#1890ff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => { if (e.ctrlKey || e.metaKey || e.button === 1) return; e.preventDefault(); navigate(`/payments/${record._id}`); }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color="blue" style={{ textTransform: 'capitalize' }}>
          {category}
        </Tag>
      ),
      filters: [
        { text: 'Fuel', value: 'fuel' },
        { text: 'Maintenance', value: 'maintenance' },
        { text: 'Insurance', value: 'insurance' },
        { text: 'Insurance Claim', value: 'insurance claim' },
        { text: 'Salary', value: 'salary' },
        { text: 'Van Rental', value: 'van rental' },
        { text: 'Van Deposits', value: 'van deposits' },
        { text: 'Tracking Charges', value: 'tracking charges' },
        { text: 'Office', value: 'office' },
        { text: 'Job', value: 'job' },
        { text: 'Charges', value: 'charges' },
        { text: 'Porter', value: 'porter' },
        { text: 'Accounting Services', value: 'accounting services' },
        { text: 'PAYE', value: 'paye' },
        { text: 'Excess Mileage', value: 'excess mileage' },
        { text: 'VAT Liability', value: 'vat liability' },
        { text: 'Other', value: 'other' },
      ],
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => formatDate(date, 'DD/MM/YYYY', true),
      sorter: (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'PL Position',
      dataIndex: 'plPosition',
      key: 'plPosition',
      render: (plPosition) => (
        <Tag color={plPosition === 'income' ? 'green' : 'volcano'} style={{ textTransform: 'capitalize' }}>
          {plPosition}
        </Tag>
      ),
      filters: [
        { text: 'Income', value: 'income' },
        { text: 'Expense', value: 'expense' },
      ],
      onFilter: (value, record) => record.plPosition === value,
    },
    {
      title: 'Van',
      dataIndex: 'relatedVan',
      key: 'van',
      render: (van) => van?.registration || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/payments/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this payment?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card>
            <Statistic
              title="Total Income"
              value={Object.keys(filters).some(key => filters[key]) ? stats.filteredIncome : stats.totalIncome}
              formatter={(value) => formatCurrency(value)}
            />
            <Statistic
              title="Income VAT"
              value={Object.keys(filters).some(key => filters[key]) ? stats.filteredIncomeVAT : stats.totalIncomeVAT}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ fontSize: '14px', color: '#666' }}
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <Statistic
              title="Total Expenses"
              value={Object.keys(filters).some(key => filters[key]) ? stats.filteredExpense : stats.totalExpense}
              formatter={(value) => formatCurrency(value)}
            />
            <Statistic
              title="Expense VAT"
              value={Object.keys(filters).some(key => filters[key]) ? stats.filteredExpenseVAT : stats.totalExpenseVAT}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ fontSize: '14px', color: '#666' }}
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        {/* First row: Date range only */}
        <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 24, flexWrap: 'wrap' }}>
          <Col flex="none">
            <Space direction="vertical" size={2} style={{ width: 320, marginRight: 128 }}>
              <span style={{ fontWeight: 500 }}>Date Range:</span>
              <div>
                <DateRangeFilter value={dateFilter} onChange={setDateFilter} placeholder="Date Range" />
              </div>
            </Space>
          </Col>
        </Row>
        {/* Second row: Search and other filters and create button */}
        <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16, flexWrap: 'wrap', display: 'flex' }}>
          <Col flex="220px" style={{ marginRight: 16 }}>
            <Input.Search
              placeholder="Search by reference, customer, etc."
              value={filters.search || ''}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              onSearch={fetchPayments}
              allowClear
              style={{ width: '100%' }}
            />
          </Col>
          <Col flex="140px">
            <Select
              style={{ width: '100%' }}
              placeholder="Category"
              allowClear
              value={filters.category || undefined}
              onChange={(value) => setFilters({ ...filters, category: value })}
            >
              <Option value="fuel">Fuel</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="insurance">Insurance</Option>
              <Option value="insurance claim">Insurance Claim</Option>
              <Option value="salary">Salary</Option>
              <Option value="van rental">Van Rental</Option>
              <Option value="van deposits">Van Deposits</Option>
              <Option value="tracking charges">Tracking Charges</Option>
              <Option value="office">Office</Option>
              <Option value="job">Job</Option>
              <Option value="charges">Charges</Option>
              <Option value="porter">Porter</Option>
              <Option value="accounting services">Accounting Services</Option>
              <Option value="paye">PAYE</Option>
              <Option value="excess mileage">Excess Mileage</Option>
              <Option value="vat liability">VAT Liability</Option>
              <Option value="other">Other</Option>
            </Select>
          </Col>
          <Col flex="140px" style={{ marginLeft: 32 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="Van"
              allowClear
              value={filters.van || undefined}
              onChange={(value) => setFilters({ ...filters, van: value })}
              showSearch
              optionFilterProp="children"
            >
              <Option value="N/A">N/A</Option>
              {vans.map(van => (
                <Option key={van._id} value={van._id}>
                  {van.registration}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="140px" style={{ marginLeft: 32 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="PL Position"
              allowClear
              value={filters.plPosition || undefined}
              onChange={(value) => setFilters({ ...filters, plPosition: value || '' })}
            >
              <Option value="">All</Option>
              <Option value="income">Income</Option>
              <Option value="expense">Expense</Option>
            </Select>
          </Col>
          <Col flex="140px" style={{ marginLeft: 32 }}>
            <Select
              style={{ width: '100%' }}
              mode="multiple"
              value={filters.status}
              onChange={value => setFilters({ ...filters, status: value })}
              allowClear
              placeholder="Status"
              maxTagCount={1}
            >
              <Option value="paid">Paid</Option>
              <Option value="pending">Pending</Option>
              <Option value="overdue">Overdue</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col flex="180px" style={{ marginLeft: 32 }}>
            <Select
              style={{ width: '100%' }}
              placeholder="Channel"
              allowClear
              value={filters.channel || undefined}
              onChange={(value) => setFilters({ ...filters, channel: value })}
              showSearch
              optionFilterProp="children"
            >
              {channels.map(channel => (
                <Option key={channel._id || channel.name} value={channel.name}>
                  {channel.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right', minWidth: 120 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/payments/create')}
              style={{ minWidth: 100 }}
            >
              Create
            </Button>
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="_id"
          loading={loading}
          style={{ marginTop: 16 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
          }}
        />
      </Card>
    </div>
  );
};

export default PaymentList; 